// FiltersGrid.tsx
import { Accordion, Col, Form, Row } from 'react-bootstrap'
import { getColor } from '../../../_utils'
import { useAuth } from '../../../auth/auth-context'
import { PublisherOrCampaign, IFilterSelectorOpts } from '../home/dashboard-content/types'

type FiltersGridProps = {
    opts: IFilterSelectorOpts
    selectedFilters: IFilterSelectorOpts
    onItemSelect: (
        filterKey: keyof FiltersGridProps['selectedFilters'],
        item: string | PublisherOrCampaign
    ) => void
    onSelectAll: (
        filterKey: keyof FiltersGridProps['selectedFilters'],
        options: string[] | PublisherOrCampaign[]
    ) => void
    allCollapsed?: boolean
    showSelectedCountInHeader?: boolean
}

export default function FiltersGrid({
    opts,
    selectedFilters,
    onItemSelect,
    onSelectAll,
    allCollapsed,
    showSelectedCountInHeader
}: FiltersGridProps) {
    const { user } = useAuth()
    const isPublisher = user?.kind === 'publisher'

    const isPublisherOrCampaign = (item: any): item is PublisherOrCampaign =>
        item && typeof item === 'object' && 'id' in item && 'name' in item

    const isItemInArray = (
        array: (string | PublisherOrCampaign)[],
        item: string | PublisherOrCampaign
    ): boolean => {
        if (typeof item === 'string') return array.includes(item)
        return array.some(
            (i) =>
                isPublisherOrCampaign(i) &&
                i.id === item.id &&
                i.network === item.network &&
                i.country === item.country
        )
    }

    const isItemDisabled = (item: PublisherOrCampaign) =>
        !selectedFilters.countries.includes(item.country) ||
        !selectedFilters.networks.includes(item.network)

    const sortOptions = (
        options: string[] | PublisherOrCampaign[]
    ): (string | PublisherOrCampaign)[] => {
        if (typeof options[0] === 'string') return options as string[]
        return (options as PublisherOrCampaign[]).sort((a, b) => {
            const isADisabled = isItemDisabled(a)
            const isBDisabled = isItemDisabled(b)
            if (isADisabled === isBDisabled)
                return a?.name?.localeCompare(b?.name) || 0
            return isADisabled ? 1 : -1
        })
    }

    const renderFlag = (opt: string | PublisherOrCampaign) => {
        const country = typeof opt === 'string' ? opt : opt.country
        if (country?.toUpperCase() === 'UNKNOWN') return null
        return (
            <img
                className="me-2"
                src={`/flags/${country?.toLowerCase()}.svg`}
                alt={country}
                style={{ height: '15px' }}
            />
        )
    }

    const renderNetwork = (opt: string | PublisherOrCampaign) => {
        const network = typeof opt === 'string' ? opt : opt.network
        const index = opts.networks.indexOf(network)
        return (
            <span
                className="me-2"
                style={{ color: getColor(network, index) }}
            >
                ●
            </span>
        )
    }

    const renderFilterSection = (
        filterKey: keyof typeof selectedFilters,
        label: string,
        options: string[] | PublisherOrCampaign[],
        collapsed?: boolean
    ) => {
        const selectedCount = selectedFilters[filterKey].length
        const totalCount = options.length

const headerLabel = showSelectedCountInHeader ? (
    <>
        {label}
        <span
            style={{color: selectedCount !== options.length ? 'var(--bs-primary)' : 'inherit'}}
            className='ms-1'
        >
            ({selectedCount}/{totalCount})
        </span>
    </>
) : (
    label
)


        return (
            <Accordion defaultActiveKey={collapsed ? undefined : label}>
                <Accordion.Item eventKey={label}>
                    <Accordion.Header>{headerLabel}</Accordion.Header>
                    <Accordion.Body
                        style={{ height: '250px', overflowY: 'auto' }}
                    >
                        <Form.Check
                            type="checkbox"
                            id={`select-all-${label}`}
                            label="Select All"
                            checked={selectedCount === totalCount}
                            onChange={() => onSelectAll(filterKey, options)}
                        />
                        <hr />
                        {sortOptions(options).filter(f=>f).map((option, index) => {
                            const labelText =
                                typeof option === 'string'
                                    ? option
                                    : option.name || 'Unknown'
                            const keyText =
                                typeof option === 'string'
                                    ? option
                                    : option.id +
                                      option.network +
                                      option.country
                            const disabled = isPublisherOrCampaign(option)
                                ? isItemDisabled(option)
                                : false
                            const selected = isItemInArray(
                                selectedFilters[filterKey],
                                option
                            )

                            return (
                                <div
                                    key={`${labelText}-${index}`}
                                    className="d-flex align-items-center w-100"
                                >
                                    <Form.Check
                                        type="checkbox"
                                        id={`${keyText}-${filterKey}`}
                                        className="me-2"
                                        checked={selected}
                                        onChange={() =>
                                            onItemSelect(filterKey, option)
                                        }
                                        disabled={disabled}
                                    />
                                    <Form.Label
                                        htmlFor={`${keyText}-${filterKey}`}
                                        className="d-flex justify-content-between w-100 mb-0"
                                    >
                                        <span>
                                            {labelText.toUpperCase() ===
                                            'UNKNOWN'
                                                ? 'Other'
                                                : labelText}
                                        </span>
                                        <div>
                                            {(isPublisherOrCampaign(option) ||
                                                filterKey === 'countries') &&
                                                renderFlag(option)}
                                            {(isPublisherOrCampaign(option) ||
                                                filterKey === 'networks') &&
                                                renderNetwork(option)}
                                        </div>
                                    </Form.Label>
                                </div>
                            )
                        })}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        )
    }

    return (
        <Row>
            <Col xs={12} md={6} className="mb-2">
                {renderFilterSection(
                    'networks',
                    'Networks',
                    opts.networks,
                    allCollapsed
                )}
            </Col>
            <Col xs={12} md={6}>
                {renderFilterSection(
                    'campaigns',
                    'Campaigns',
                    opts.campaigns,
                    allCollapsed
                )}
            </Col>
            <Col xs={12} md={6} className="mb-2">
                {renderFilterSection(
                    'countries',
                    'Countries',
                    opts.countries,
                    true
                )}
            </Col>
            {!isPublisher ? (
                <Col xs={12} md={6} className="mb-2">
                    {renderFilterSection(
                        'publishers',
                        'Publishers',
                        opts.publishers,
                        true
                    )}
                </Col>
            ) : (
                <Col xs={12} md={6} className="mb-2">
                    {renderFilterSection(
                        'channels',
                        'Channels',
                        opts.channels,
                        true
                    )}
                </Col>
            )}
        </Row>
    )
}
